import {
  INTRANET_FILE_MAX_BYTES,
  INTRANET_IMAGE_ACCEPT,
  INTRANET_IMAGE_MAX_BYTES,
  INTRANET_SEARCH_MIN_QUERY,
  INTRANET_VIDEO_ACCEPT,
  INTRANET_VIDEO_MAX_BYTES,
  intranetFileDownloadPath,
  intranetImageMaxMb,
  isIntranetHtmlBody,
  type IntranetColleague,
  type IntranetFile,
} from '@gb-ticket/shared-types';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { type Editor, EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  BoldIcon,
  CodeIcon,
  EllipsisIcon,
  ImageIcon,
  ImagePlayIcon,
  ItalicIcon,
  LinkIcon,
  ListIcon,
  ListOrderedIcon,
  PaperclipIcon,
  SmileIcon,
  StrikethroughIcon,
  UnderlineIcon,
  VideoIcon,
} from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { API_BASE_URL, ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { IntranetAiDraftButton } from '@/products/intranet/components/intranet-ai-draft-button';
import { useVoice } from '@/platform/voice/use-voice';
import { colleagueVisibleName } from '@/products/intranet/components/colleague-name-button';
import {
  INTRANET_FILE_CHECK_HINT,
  IntranetInlineImage,
  IntranetInlineVideo,
  IntranetPendingFile,
  intranetStoredFileSrc,
  takePickedFiles,
  type IntranetAttachedFile,
  type IntranetMediaRole,
} from '@/products/intranet/features/intranet/editor-media';
import {
  IntranetMention,
  insertMentionNode,
  type MentionTrigger,
  mentionTriggerAt,
} from '@/products/intranet/features/intranet/editor-mention';
import { useIntranetAppearance } from '@/products/intranet/features/intranet/use-intranet';
import { useIntranetColleagues } from '@/products/intranet/features/intranet/use-intranet-directory';
import { useIntranetFiles } from '@/products/intranet/features/intranet/use-intranet-library';

const MENTION_DEBOUNCE_MS = 300;
const MENTION_MAX_OPTIONS = 8;

export const INTRANET_EDITOR_PLACEHOLDER = 'Text schreiben. Mit @ Kollegen erwähnen.';

/** Fixed list; no external emoji service. */
const INTRANET_EMOJIS = [
  '😀',
  '😊',
  '😂',
  '😍',
  '🤔',
  '😮',
  '👍',
  '👏',
  '🙏',
  '👋',
  '💪',
  '🎉',
  '🥳',
  '🎂',
  '❤️',
  '🔥',
  '✅',
  '⚠️',
  '📌',
  '📅',
  '📣',
  '💡',
  '☕',
  '🚗',
] as const;

type BlockStyle = 'paragraph' | 'h2' | 'h3';

const BLOCK_STYLES: { value: BlockStyle; label: string }[] = [
  { value: 'paragraph', label: 'Normal' },
  { value: 'h2', label: 'Überschrift' },
  { value: 'h3', label: 'Unterüberschrift' },
];

function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

function escapeHtml(text: string): string {
  return text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

/** Older bodies are plain text; blank lines become paragraphs, single breaks stay breaks. */
function toEditorHtml(value: string): string {
  if (!value.trim()) {
    return '';
  }
  if (isIntranetHtmlBody(value)) {
    return value;
  }
  return value
    .split(/\n{2,}/)
    .map((block) => `<p>${escapeHtml(block).replaceAll('\n', '<br>')}</p>`)
    .join('');
}

/**
 * Rich-text editor for news bodies. Emits HTML; the server sanitises it before storing.
 * Mounted once per form: later `value` changes from outside are not pushed back into the editor.
 */
export function IntranetBodyEditor({
  labelId,
  value,
  onChange,
  slug,
  title = '',
  teaser = '',
  onApplyDraft,
  onAttach,
  onOpenPoll,
}: {
  labelId: string;
  value: string;
  onChange: (html: string) => void;
  /** Current title and teaser, sent with the draft request together with the body. */
  title?: string;
  teaser?: string;
  /** Writes title and teaser into the same form. The body is applied in this editor. */
  onApplyDraft?: (suggestion: { title?: string; teaser?: string }) => void;
  /** Set once the entry exists. Pending inline files are polled against it. */
  slug?: string;
  /** One upload for Bild, Video, GIF, Datei and Anhang. */
  onAttach?: (file: File, role: IntranetMediaRole) => Promise<IntranetAttachedFile>;
  /** Opens the poll under the text field. Voting stays off in the form. */
  onOpenPoll?: () => void;
}) {
  const voice = useVoice();
  const [trigger, setTrigger] = useState<MentionTrigger | null>(null);
  const [mentionQ, setMentionQ] = useState('');
  const [active, setActive] = useState(0);
  const mentionOpen = trigger !== null && mentionQ.length >= INTRANET_SEARCH_MIN_QUERY;
  const colleagues = useIntranetColleagues({ q: mentionQ }, mentionOpen);
  const appearance = useIntranetAppearance();
  const options = mentionOpen ? (colleagues.data?.items ?? []).slice(0, MENTION_MAX_OPTIONS) : [];
  const keys = useRef<MentionKeys>({ options: [], active: 0, pick: () => undefined });
  const imageRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const gifRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [localFiles, setLocalFiles] = useState<IntranetAttachedFile[]>([]);
  const waiting = value.includes('data-intranet-file');
  const files = useIntranetFiles(slug, waiting ? 3000 : false);

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, underline: false, link: false }),
      Underline,
      Link.configure({
        openOnClick: false,
        defaultProtocol: 'https',
        protocols: ['https'],
        isAllowedUri: (url) => isHttpsUrl(url),
        HTMLAttributes: { rel: 'noopener noreferrer', target: null },
      }),
      Placeholder.configure({ placeholder: INTRANET_EDITOR_PLACEHOLDER }),
      IntranetMention,
      IntranetInlineImage,
      IntranetInlineVideo,
      IntranetPendingFile,
    ],
    content: toEditorHtml(value),
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-labelledby': labelId,
        class: 'intranet-prose min-h-64 px-3 py-2 outline-none',
      },
      handleKeyDown: (_view, event) => {
        const { options: list, active: index, pick } = keys.current;
        if (list.length === 0) {
          return false;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          const step = event.key === 'ArrowDown' ? 1 : -1;
          setActive((index + step + list.length) % list.length);
          return true;
        }
        const chosen = list[index] ?? list[0];
        if ((event.key === 'Enter' || event.key === 'Tab') && chosen) {
          pick(chosen);
          return true;
        }
        if (event.key === 'Escape') {
          setTrigger(null);
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: instance }) => {
      onChange(instance.isEmpty ? '' : instance.getHTML());
      setTrigger(mentionTriggerAt(instance));
    },
    onSelectionUpdate: ({ editor: instance }) => {
      setTrigger(mentionTriggerAt(instance));
    },
  });

  useEffect(() => {
    const query = trigger?.query ?? '';
    const handle = window.setTimeout(() => {
      setMentionQ(query.length >= INTRANET_SEARCH_MIN_QUERY ? query : '');
      setActive(0);
    }, MENTION_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [trigger?.query]);

  function pick(colleague: IntranetColleague) {
    if (!editor) {
      return;
    }
    const current = mentionTriggerAt(editor) ?? trigger;
    if (current) {
      insertMentionNode(editor, current, {
        ...colleague,
        displayName: colleagueVisibleName(colleague.displayName, voice),
      });
    }
    setTrigger(null);
    setMentionQ('');
  }

  useEffect(() => {
    keys.current = { options, active, pick };
  });

  useEffect(() => {
    if (!editor || !files.data) {
      return;
    }
    const byId = new Map(files.data.items.map((file) => [file.id, file]));
    const jobs: { from: number; to: number; file: IntranetFile }[] = [];
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name !== 'intranetPendingFile') {
        return;
      }
      const file = byId.get(node.attrs.fileId as string);
      if (!file || file.scanStatus === 'PENDING') {
        return;
      }
      jobs.push({ from: pos, to: pos + node.nodeSize, file });
    });
    if (jobs.length === 0) {
      return;
    }
    let tr = editor.state.tr;
    for (const job of jobs.reverse()) {
      if (job.file.scanStatus === 'BLOCKED') {
        tr = tr.delete(job.from, job.to);
        continue;
      }
      const node = job.file.mimeType.startsWith('video/')
        ? editor.schema.nodes.intranetVideo?.create({
            src: intranetStoredFileSrc(job.file.id),
          })
        : editor.schema.nodes.intranetImage?.create({
            src: intranetStoredFileSrc(job.file.id),
            alt: job.file.filename,
          });
      if (node) {
        tr = tr.replaceWith(job.from, job.to, node);
      }
    }
    if (tr.docChanged) {
      editor.view.dispatch(tr);
    }
  }, [editor, files.data]);

  async function openMedia(role: IntranetMediaRole) {
    await appearance.refetch();
    const input = { image: imageRef, video: videoRef, gif: gifRef, file: fileRef }[role];
    input.current?.click();
  }

  async function onPicked(role: IntranetMediaRole, list: FileList | null, input: HTMLInputElement) {
    const picked = takePickedFiles(list, input);
    if (picked.length === 0 || !onAttach || !editor) {
      return;
    }
    const refreshed = await appearance.refetch();
    const limit = refreshed.data?.contentImageMaxBytes ?? INTRANET_IMAGE_MAX_BYTES;
    const files = role === 'image' ? picked : [picked[0]!];
    let inserted = 0;

    for (const file of files) {
      const reason = mediaRejectReason(file, role, limit);
      if (reason) {
        toast.error(reason);
        continue;
      }
      try {
        const saved = await onAttach(file, role);
        if (role === 'file') {
          setLocalFiles((current) => [...current, saved]);
          continue;
        }
        if (saved.scanStatus === 'BLOCKED') {
          toast.error('Die Datei wurde abgelehnt und wird nicht angezeigt.');
          continue;
        }
        if (saved.scanStatus === 'PENDING') {
          editor
            .chain()
            .focus('end')
            .insertContent({ type: 'intranetPendingFile', attrs: { fileId: saved.id } })
            .run();
          inserted += 1;
          continue;
        }
        editor
          .chain()
          .focus('end')
          .insertContent(
            saved.mimeType.startsWith('video/')
              ? { type: 'intranetVideo', attrs: { src: intranetStoredFileSrc(saved.id) } }
              : {
                  type: 'intranetImage',
                  attrs: { src: intranetStoredFileSrc(saved.id), alt: saved.filename },
                },
          )
          .run();
        inserted += 1;
      } catch (error) {
        toast.error(error instanceof ApiRequestError ? error.message : 'Upload fehlgeschlagen.');
      }
    }

    if (role === 'image' && inserted > 1) {
      toast.success(`${inserted} Bilder eingefügt`);
    }
  }

  const serverAttachments = (files.data?.items ?? []).filter((file) => !value.includes(file.id));
  const attachments = [
    ...localFiles.filter((file) => !serverAttachments.some((server) => server.id === file.id)),
    ...serverAttachments,
  ];

  return (
    <div className="relative rounded-md border bg-background text-foreground shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
      <Toolbar
        editor={editor}
        mediaReady={editor !== null && onAttach !== undefined}
        openMedia={openMedia}
      />
      <EditorContent editor={editor} />
      <div className="flex flex-wrap gap-2 border-t p-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!onAttach}
          onClick={() => void openMedia('file')}
        >
          Datei
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!onAttach}
          onClick={() => void openMedia('gif')}
        >
          GIF
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!onOpenPoll}
          onClick={onOpenPoll}
        >
          Umfrage
        </Button>
        <IntranetAiDraftButton
          title={title}
          teaser={teaser}
          bodyHtml={editor && !editor.isEmpty ? editor.getHTML() : value}
          onApply={(suggestion) => {
            if (editor && suggestion.bodyHtml) {
              editor.commands.setContent(toEditorHtml(suggestion.bodyHtml));
              onChange(editor.isEmpty ? '' : editor.getHTML());
            }
            onApplyDraft?.({ title: suggestion.title, teaser: suggestion.teaser });
          }}
        />
      </div>
      {attachments.length > 0 ? (
        <ul className="space-y-1 border-t px-3 py-2 text-sm">
          {attachments.map((file) => (
            <li key={file.id}>
              {'downloadable' in file && file.downloadable === false ? (
                <span>{file.filename}</span>
              ) : file.scanStatus === 'CLEAN' || file.scanStatus === 'SKIPPED' ? (
                <a
                  href={`${API_BASE_URL}${intranetFileDownloadPath(file.id)}`}
                  className="text-primary underline"
                >
                  {file.filename}
                </a>
              ) : file.scanStatus === 'BLOCKED' ? (
                <span className="text-destructive">Die Datei wurde abgelehnt.</span>
              ) : (
                <span className="text-muted-foreground">
                  {file.filename}: {INTRANET_FILE_CHECK_HINT}
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      <input
        ref={imageRef}
        type="file"
        accept={INTRANET_IMAGE_ACCEPT}
        multiple
        className="sr-only"
        aria-label="Bild auswählen"
        onChange={(event) => void onPicked('image', event.target.files, event.target)}
      />
      <input
        ref={videoRef}
        type="file"
        accept={INTRANET_VIDEO_ACCEPT}
        className="sr-only"
        aria-label="Video auswählen"
        onChange={(event) => void onPicked('video', event.target.files, event.target)}
      />
      <input
        ref={gifRef}
        type="file"
        accept="image/gif,.gif"
        className="sr-only"
        aria-label="GIF auswählen"
        onChange={(event) => void onPicked('gif', event.target.files, event.target)}
      />
      <input
        ref={fileRef}
        type="file"
        className="sr-only"
        aria-label="Anhang auswählen"
        onChange={(event) => void onPicked('file', event.target.files, event.target)}
      />
      {mentionOpen ? (
        <ul
          role="listbox"
          aria-label="Kollegen erwähnen"
          className="absolute inset-x-0 top-full z-20 mt-1 max-h-60 overflow-y-auto rounded-lg border bg-popover shadow-md"
        >
          {colleagues.isPending ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">Kollegen werden gesucht …</li>
          ) : options.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">
              Keine Kollegin oder kein Kollege.
            </li>
          ) : (
            options.map((colleague, index) => (
              <li
                key={colleague.id}
                role="option"
                aria-selected={index === active}
                className={cn(
                  'flex min-h-11 cursor-pointer items-center px-3 text-sm hover:bg-muted',
                  index === active && 'bg-muted',
                )}
                onMouseDown={(event) => {
                  // Keep the caret in the editor so the @-token can be replaced.
                  event.preventDefault();
                  pick(colleague);
                }}
              >
                {colleagueVisibleName(colleague.displayName, voice)}
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

type MentionKeys = {
  options: IntranetColleague[];
  active: number;
  pick: (colleague: IntranetColleague) => void;
};

function Toolbar({
  editor,
  mediaReady,
  openMedia,
}: {
  editor: Editor | null;
  mediaReady: boolean;
  openMedia: (role: IntranetMediaRole) => void | Promise<void>;
}) {
  const [linkOpen, setLinkOpen] = useState(false);
  const state = useEditorState({
    editor,
    selector: ({ editor: instance }) => ({
      block: instance?.isActive('heading', { level: 2 })
        ? 'h2'
        : instance?.isActive('heading', { level: 3 })
          ? 'h3'
          : 'paragraph',
      bold: instance?.isActive('bold') ?? false,
      italic: instance?.isActive('italic') ?? false,
      underline: instance?.isActive('underline') ?? false,
      orderedList: instance?.isActive('orderedList') ?? false,
      bulletList: instance?.isActive('bulletList') ?? false,
      link: instance?.isActive('link') ?? false,
    }),
  });
  const ready = editor !== null;

  function setBlock(style: BlockStyle) {
    const chain = editor?.chain().focus();
    if (style === 'paragraph') {
      chain?.setParagraph().run();
    } else {
      chain?.setHeading({ level: style === 'h2' ? 2 : 3 }).run();
    }
  }

  function refocus(event: Event) {
    event.preventDefault();
    editor?.commands.focus();
  }

  return (
    <div
      role="toolbar"
      aria-label="Formatierung"
      className="flex flex-wrap items-center gap-1 border-b p-1"
    >
      <Select
        value={state?.block ?? 'paragraph'}
        disabled={!ready}
        onValueChange={(value) => setBlock(value as BlockStyle)}
      >
        <SelectTrigger aria-label="Absatzstil" size="sm" className="w-auto">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {BLOCK_STYLES.map((style) => (
            <SelectItem key={style.value} value={style.value}>
              {style.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <ToolbarButton
        label="Fett"
        pressed={state?.bold}
        disabled={!ready}
        onClick={() => editor?.chain().focus().toggleBold().run()}
      >
        <BoldIcon />
      </ToolbarButton>
      <ToolbarButton
        label="Kursiv"
        pressed={state?.italic}
        disabled={!ready}
        onClick={() => editor?.chain().focus().toggleItalic().run()}
      >
        <ItalicIcon />
      </ToolbarButton>
      <ToolbarButton
        label="Unterstrichen"
        pressed={state?.underline}
        disabled={!ready}
        onClick={() => editor?.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon />
      </ToolbarButton>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="Weitere Formatierung"
            title="Weitere Formatierung"
            disabled={!ready}
          >
            <EllipsisIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" onCloseAutoFocus={refocus}>
          <DropdownMenuItem onSelect={() => editor?.chain().focus().toggleStrike().run()}>
            <StrikethroughIcon aria-hidden />
            Durchgestrichen
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => editor?.chain().focus().toggleCode().run()}>
            <CodeIcon aria-hidden />
            Code
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ToolbarButton
        label="Nummerierte Liste"
        pressed={state?.orderedList}
        disabled={!ready}
        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
      >
        <ListOrderedIcon />
      </ToolbarButton>
      <ToolbarButton
        label="Aufzählung"
        pressed={state?.bulletList}
        disabled={!ready}
        onClick={() => editor?.chain().focus().toggleBulletList().run()}
      >
        <ListIcon />
      </ToolbarButton>
      <ToolbarButton
        label="Link"
        pressed={state?.link}
        disabled={!ready}
        onClick={() => setLinkOpen(true)}
      >
        <LinkIcon />
      </ToolbarButton>
      <ToolbarButton label="Bild" disabled={!mediaReady} onClick={() => void openMedia('image')}>
        <ImageIcon />
      </ToolbarButton>
      <ToolbarButton label="Video" disabled={!mediaReady} onClick={() => void openMedia('video')}>
        <VideoIcon />
      </ToolbarButton>
      <ToolbarButton label="GIF" disabled={!mediaReady} onClick={() => void openMedia('gif')}>
        <ImagePlayIcon />
      </ToolbarButton>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="Emoji"
            title="Emoji"
            disabled={!ready}
          >
            <SmileIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="grid grid-cols-6 gap-0.5"
          onCloseAutoFocus={refocus}
        >
          {INTRANET_EMOJIS.map((emoji) => (
            <DropdownMenuItem
              key={emoji}
              className="justify-center text-lg"
              onSelect={() => editor?.chain().focus().insertContent(emoji).run()}
            >
              {emoji}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <ToolbarButton label="Anhang" disabled={!mediaReady} onClick={() => void openMedia('file')}>
        <PaperclipIcon />
      </ToolbarButton>
      {editor ? <LinkDialog editor={editor} open={linkOpen} onOpenChange={setLinkOpen} /> : null}
    </div>
  );
}

function LinkDialog({
  editor,
  open,
  onOpenChange,
}: {
  editor: Editor;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {open ? <LinkForm editor={editor} onDone={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function LinkForm({ editor, onDone }: { editor: Editor; onDone: () => void }) {
  const current = editor.getAttributes('link').href as string | undefined;
  const [url, setUrl] = useState(current ?? 'https://');
  const [error, setError] = useState<string | null>(null);

  function onSubmit(event: FormEvent) {
    // The dialog is portalled but still inside the page form in the React tree.
    event.preventDefault();
    event.stopPropagation();
    const href = url.trim();
    if (href === '' || href === 'https://') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      onDone();
      return;
    }
    if (!isHttpsUrl(href)) {
      setError('Bitte eine Adresse mit https:// eingeben.');
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    onDone();
  }

  return (
    <form className="grid gap-4" onSubmit={onSubmit} noValidate>
      <DialogHeader>
        <DialogTitle>Link einfügen</DialogTitle>
        <DialogDescription>
          Nur Adressen mit https://. Leer lassen entfernt den Link.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        <Label htmlFor="intranet-link-url">Adresse</Label>
        <Input
          id="intranet-link-url"
          type="url"
          inputMode="url"
          value={url}
          aria-invalid={error !== null}
          onChange={(event) => {
            setUrl(event.target.value);
            setError(null);
          }}
        />
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDone}>
          Abbrechen
        </Button>
        <Button type="submit">Übernehmen</Button>
      </DialogFooter>
    </form>
  );
}

const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const VIDEO_TYPES = new Set(['video/mp4', 'video/webm']);

function mediaRejectReason(
  file: File,
  role: IntranetMediaRole,
  contentImageMaxBytes: number,
): string | null {
  if (role === 'image') {
    if (!IMAGE_TYPES.has(file.type)) {
      return 'Erlaubt sind JPEG, PNG und WebP.';
    }
    if (file.size > contentImageMaxBytes) {
      return `Das Bild darf höchstens ${intranetImageMaxMb(contentImageMaxBytes)} MB groß sein.`;
    }
    return null;
  }
  if (role === 'file' && IMAGE_TYPES.has(file.type)) {
    if (file.size > contentImageMaxBytes) {
      return `Das Bild darf höchstens ${intranetImageMaxMb(contentImageMaxBytes)} MB groß sein.`;
    }
    return null;
  }
  if (file.size > INTRANET_FILE_MAX_BYTES) {
    return 'Die Datei darf höchstens 10 MB groß sein.';
  }
  if (role === 'gif' && file.type !== 'image/gif') {
    return 'Bitte eine GIF-Datei wählen.';
  }
  if (role === 'video' && (!VIDEO_TYPES.has(file.type) || file.size > INTRANET_VIDEO_MAX_BYTES)) {
    return 'Das Video muss unter 12 MB bleiben (MP4 oder WebM).';
  }
  return null;
}

function ToolbarButton({
  label,
  pressed,
  disabled,
  onClick,
  children,
}: {
  label: string;
  pressed?: boolean;
  disabled: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant={pressed ? 'secondary' : 'ghost'}
      size="sm"
      aria-label={label}
      aria-pressed={pressed}
      title={label}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}
